/**
 * VoucherProgramsTab - Settings > Claims > Vouchers.
 *
 * A program says which payment type becomes a voucher, where its numbers
 * come from and how long it lasts. Payment types come from the tenant's own
 * `claim_payment.payment_type` options and number sources from the API, so
 * nothing here lists a tenant's values.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Pencil, Plus, Ticket } from 'lucide-react'
import { toast } from 'sonner'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import { useEntityTypeByKey } from '@/admin/hooks/use-entities'
import {
  useSaveVoucherProgram,
  useVoucherPrograms,
  type VoucherProgram,
} from '@/admin/hooks/use-vouchers'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm, type FormContext, type FormField } from '@/shared/lib/forms'

type Option = { value: string; label: string }

/** A stable key from a name: lower case, runs of anything else become `_`. */
function keyFrom(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64)
}

export function VoucherProgramsTab() {
  const { t } = useTranslation()
  const { data, isPending, isFetching, isError, refetch } = useVoucherPrograms()
  const { data: paymentEntity, isConfigLoading: paymentTypesLoading } =
    useEntityTypeByKey('claim_payment')
  // The dialog reads both option lists once, so it waits for them.
  const optionsReady = !isPending && !paymentTypesLoading
  const [editing, setEditing] = useState<VoucherProgram | 'new' | null>(null)

  const paymentTypes = useMemo<Option[]>(() => {
    const attribute = paymentEntity?.attributes.find((a) => a.code_name === 'payment_type')
    const options = (attribute?.configuration_data.options as Option[] | undefined) ?? []
    return options.map((o) => ({ value: o.value, label: o.label }))
  }, [paymentEntity])
  const numberSources = useMemo(() => data?.number_sources ?? [], [data])
  const programs = data?.programs ?? []

  const labelOf = (options: Option[], value: string) =>
    options.find((o) => o.value.toLowerCase() === value.toLowerCase())?.label ?? value

  const columns = useMemo<DataTableColumn<VoucherProgram>[]>(
    () => [
      {
        id: 'label',
        header: t('voucherPrograms.name', 'Name'),
        cell: (row) => <span className="text-[13px] text-[var(--ink)]">{row.label}</span>,
      },
      {
        id: 'payment_type',
        header: t('voucherPrograms.paymentType', 'Payment type'),
        cell: (row) => labelOf(paymentTypes, row.payment_type),
      },
      {
        id: 'number_source',
        header: t('voucherPrograms.numbers', 'Numbers'),
        cell: (row) => labelOf(numberSources, row.number_source),
      },
      {
        id: 'valid_for_days',
        header: t('voucherPrograms.expiry', 'Expiry'),
        cell: (row) =>
          row.valid_for_days
            ? t('voucherPrograms.days', {
                defaultValue: '{{count}} days',
                count: row.valid_for_days,
              })
            : t('voucherPrograms.never', 'Never expires'),
      },
      {
        id: 'status',
        header: t('voucherPrograms.status', 'Status'),
        cell: (row) => (
          <StatusBadge
            status={
              row.is_active
                ? t('voucherPrograms.active', 'Active')
                : t('voucherPrograms.inactive', 'Inactive')
            }
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        align: 'right',
        cell: (row) => (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={t('voucherPrograms.edit', 'Edit program')}
            data-testid={`voucher-program-edit-${row.key}`}
            onClick={() => setEditing(row)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        ),
      },
    ],
    [t, paymentTypes, numberSources],
  )

  return (
    <div className="mt-6 flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="clara-label text-[var(--ink-3)]">
            {t('voucherPrograms.eyebrow', 'Settlement config')}
          </p>
          <h2 className="mt-1 text-[18px] text-[var(--ink)]">
            {t('voucherPrograms.title', 'Vouchers')}
          </h2>
          <p className="mt-1 max-w-[720px] text-[12px] leading-relaxed text-[var(--ink-3)]">
            {t(
              'voucherPrograms.description',
              'Which payment types are paid by voucher, where voucher numbers come from and how long a voucher lasts. A payment of a program\'s type is held until its voucher is redeemed.',
            )}
          </p>
        </div>
        <Button size="sm" onClick={() => setEditing('new')} data-testid="voucher-program-add">
          <Plus className="h-3.5 w-3.5" />
          {t('voucherPrograms.add', 'Add program')}
        </Button>
      </div>

      <DataTable<VoucherProgram>
        tableId="voucher-programs"
        columns={columns}
        data={programs}
        totalCount={programs.length}
        isLoading={isPending}
        isFetching={isFetching}
        isError={isError}
        onRetry={() => void refetch()}
        rowKey="id"
        rowTestId={(row) => `voucher-program-${row.key}`}
        emptyStateNode={
          <EmptyState
            icon={Ticket}
            title={t('voucherPrograms.emptyTitle', 'No voucher programs')}
            description={t(
              'voucherPrograms.emptyBody',
              'Add a program to pay a payment type by voucher.',
            )}
          />
        }
      />

      {editing !== null && optionsReady && (
        <ProgramDialog
          program={editing === 'new' ? null : editing}
          paymentTypes={paymentTypes}
          numberSources={numberSources}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

const FORM_ID = 'voucher-program-form'

function ProgramDialog({
  program,
  paymentTypes,
  numberSources,
  onClose,
}: {
  program: VoucherProgram | null
  paymentTypes: Option[]
  numberSources: Option[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const save = useSaveVoucherProgram()
  const isNew = program === null

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'label',
        label: t('voucherPrograms.name', 'Name'),
        value: program?.label ?? '',
        required: t('voucherPrograms.nameRequired', 'Give the program a name.'),
      },
      {
        type: 'select',
        name: 'payment_type',
        label: t('voucherPrograms.paymentType', 'Payment type'),
        value: program?.payment_type ?? '',
        options: paymentTypes,
        required: t('voucherPrograms.paymentTypeRequired', 'Pick the payment type paid by voucher.'),
        hint: t(
          'voucherPrograms.paymentTypeHint',
          'Payment types come from Settings, Claims, Payment Types.',
        ),
      },
      {
        type: 'select',
        name: 'number_source',
        label: t('voucherPrograms.numbers', 'Numbers'),
        value: program?.number_source ?? numberSources[0]?.value ?? '',
        options: numberSources,
        required: t('voucherPrograms.numbersRequired', 'Pick where voucher numbers come from.'),
      },
      {
        type: 'checkbox',
        name: 'expires',
        label: t('voucherPrograms.expires', 'Vouchers expire'),
        value: Boolean(program?.valid_for_days),
      },
      {
        type: 'number',
        name: 'valid_for_days',
        label: t('voucherPrograms.validFor', 'Days a voucher lasts'),
        value: program?.valid_for_days ?? '',
        hint: t('voucherPrograms.validForHint', 'Counted from the day the voucher is made.'),
        visible: (ctx: FormContext) => Boolean(ctx.values.expires),
      },
      ...(isNew
        ? []
        : [
            {
              type: 'checkbox' as const,
              name: 'is_active',
              label: t('voucherPrograms.isActive', 'Active: new payments of this type get vouchers'),
              value: program.is_active,
            },
          ]),
    ],
    [t, program, isNew, paymentTypes, numberSources],
  )

  const { Form } = useForm(
    { name: FORM_ID, submit: 'footer', fields },
    async (values, formCtx) => {
      const v = values as Record<string, unknown>
      const expires = Boolean(v.expires)
      const days = Number(v.valid_for_days)
      if (expires && !(Number.isInteger(days) && days >= 1 && days <= 3650)) {
        formCtx.setFieldError(
          'valid_for_days',
          t('voucherPrograms.validForInvalid', 'Enter a whole number of days, 1 to 3650.'),
        )
        return
      }
      const label = String(v.label ?? '').trim()
      try {
        await save.mutateAsync({
          ...(isNew ? { key: keyFrom(label) } : { id: program.id, key: program.key }),
          label,
          payment_type: String(v.payment_type ?? ''),
          number_source: String(v.number_source ?? ''),
          valid_for_days: expires ? days : null,
          ...(isNew ? {} : { is_active: Boolean(v.is_active) }),
        })
        toast.success(
          isNew
            ? t('voucherPrograms.added', 'Voucher program added.')
            : t('voucherPrograms.saved', 'Voucher program saved.'),
        )
        onClose()
      } catch (err) {
        toastApiError(err, t('voucherPrograms.failed', 'The program could not be saved.'))
      }
    },
    [isNew, program, save, onClose, t],
  )

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? t('voucherPrograms.addTitle', 'Add a voucher program')
              : t('voucherPrograms.editTitle', 'Edit voucher program')}
          </DialogTitle>
          <DialogDescription>
            {isNew
              ? t(
                  'voucherPrograms.addBody',
                  'Its key is made from the name and cannot change once saved.',
                )
              : t('voucherPrograms.editBody', {
                  defaultValue: 'Key: {{key}}. Changes apply to vouchers made from now on.',
                  key: program.key,
                })}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Form className="flex flex-col gap-4" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} disabled={save.isPending}>
            {save.isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
            {t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
